import { Link } from '../../components/Link';
import LinkButton from '../../components/LinkButton';
import {
  VotingBadge,
  VotingBadges,
} from '../../components/Votings/VotingBadges';
import { getVotings } from '../../features/queries/getVotings';
import { useInfiniteQ } from '../../lib/query';
import {
  citizenVotingStatus,
  plainDescription,
  votingDetailPath,
  votingState,
  type VotingState,
} from '../../lib/votings';
import { useLayout } from '../../providers/LayoutProvider';
import {
  __,
  declension,
  formatDate,
  getVotesDeclension,
  toastError,
} from '../../utils';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import { alpha } from '@mui/material/styles';
import { useAuth } from '@presov.sk/presov-auth-react';
import {
  Box,
  Button,
  Card,
  EmptyState,
  MarkdownPreview,
  Skeleton,
  Stack,
  Typography,
  type StatusChipTone,
} from '@presov.sk/presov-ui-react';
import { differenceInCalendarDays } from 'date-fns';
import React from 'react';

/**
 * **Strana je maximum, ktoré API dovolí (100).** `getVotings` pozná len `limit`
 * a `pagecursor`, takže jedna strana takto drží celý zoznam hlasovaní mesta na
 * roky dopredu; keby ho raz prerástol, ďalšie strany si dopĺňa „Zobraziť
 * ďalšie". Hľadanie ani filter tu **zatiaľ nie sú** - hlasovaní je málo a stránka
 * ich ukazuje všetky; keď ich raz bude priveľa, filter podľa stavu má zmysel až
 * s `q` a filtrom v API (nad jednou stranou by prehľadal len ju).
 */
const LIMIT = 100;

/** Koľko ukončených sa vypíše, kým sa skupina nerozbalí celá. */
const CLOSED_PREVIEW = 5;

/**
 * Stav, ktorý občan môže vidieť. `archived` medzi nimi nie je: archivované
 * (zabudnuté) hlasovanie z API nepríde, občianske dotazy ho filtrujú.
 */
type CitizenState = Exclude<VotingState, 'archived'>;

type Voting = {
  voting_id: string;
  title: string;
  description?: string;
  open_from?: string;
  open_to?: string;
  votes_count: number;
  verified_accounts_only?: boolean;
  /**
   * Odovzdal tento účet v hlasovaní hlas? Rozhoduje o akcii karty - kto
   * hlasoval, dostáva „Moje odpovede", nie „Hlasovať".
   *
   * Je to **nepovinné pole a typ si ho nesie táto stránka, nie generovaný
   * klient**: pribudlo do `GET /votings` v `digiobcan-api` a do `src/api/`
   * sa dostane až s vydaným openapi balíkom (6.0.1 ho na `VotingListItem`
   * ešte nemá). Do vtedy je `undefined` a zoznam sa správa ako predtým -
   * `undefined` nie je `false`, ale pre zobrazenie to vychádza rovnako.
   */
  already_voted?: boolean;
};

/**
 * Koľko dní zostáva do konca hlasovania; `null`, keď hlasovanie nemá termín.
 * Oddelené od textu aj tónu, lebo to isté číslo potrebujú obe - chip aj stat
 * box na hlavnej karte.
 */
const remainingDays = (openTo: string | undefined, now: Date): number | null =>
  openTo ? differenceInCalendarDays(new Date(openTo), now) : null;

/** „Zostáva 26 dní" - konkrétnejšie než dátum, a dátum je v stat boxe/detaile. */
const remainingLabel = (days: number | null): string => {
  if (days === null) return __('Bez dátumu ukončenia');
  if (days <= 0) return __('Končí dnes');
  if (days === 1) return __('Zostáva 1 deň');
  if (days < 5) return `${__('Zostávajú')} ${days} ${__('dni')}`;
  return `${__('Zostáva')} ${days} ${__('dní')}`;
};

/**
 * Tón podľa naliehavosti: posledný deň je červený, posledné tri žlté. Je to
 * jediná farba na karte, ktorá sa mení v čase, a práve to má občan v zozname
 * prečítať prvé.
 */
const remainingTone = (days: number | null): StatusChipTone => {
  if (days === null) return 'neutral';
  if (days <= 1) return 'error';
  if (days <= 3) return 'warning';
  return 'info';
};

const SectionTitle: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Typography
    variant='overline'
    sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}
  >
    {children}
  </Typography>
);

/**
 * Karty prebiehajúcich a pripravovaných: **jeden stĺpec na telefóne, najviac
 * štyri na širokej obrazovke.** Stránka nemá strop šírky, takže bez stropu
 * stĺpcov by na 2560 px stálo pod sebou pár kariet dlhých ako celá obrazovka;
 * a naopak viac než štyri stĺpce už z karty urobia dlaždicu, do ktorej sa
 * názov hlasovania nezmestí.
 */
const CardGrid: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gap: 1.25,
      gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, minmax(0, 1fr))',
        lg: 'repeat(3, minmax(0, 1fr))',
        xl: 'repeat(4, minmax(0, 1fr))',
      },
    }}
  >
    {children}
  </Box>
);

/**
 * Popis na karte v mriežke sa kráti na dva riadky. Pri štyroch stĺpcoch je
 * karta úzka, takže jednoriadkové skrátenie z popisu nechá tri slová; tri
 * riadky by zase rozhodili výšku mriežky. Celý text je v `title` bunky -
 * a na hlavnej karte sa nekráti vôbec.
 */
const twoLines = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
  minWidth: 0,
} as const;

/**
 * Akcia karty. Jedno miesto - inak sa rozíde s tým, čo dovolí detail.
 *
 * **Popis je vždy „Otvoriť" a cieľ vždy detail, v každom stave a na hlavnej karte
 * tiež.** Karta je pozvánka, nie hlasovanie ani výsledok: vedie na detail, kde
 * je celé zadanie, pod ním sa hlasuje a odtiaľ vedie odkaz na výsledky, ak ich
 * hlasovanie zverejňuje. Kým tu stáli tri rôzne popisy („Hlasovať", „Výsledky",
 * „Prečítať zadanie"), každý sľuboval krok, ktorý po kliknutí neprišiel - a pri
 * hlasovaní so skrytými výsledkami by „Výsledky" viedli na stránku
 * s vysvetlením, prečo tam nič nie je. Mení sa len dôraz tlačidla.
 */
const cardAction = (voting: Voting, state: CitizenState) => ({
  to: votingDetailPath(voting.voting_id),
  label: __('Otvoriť'),
  variant:
    state === 'open' && !voting.already_voted
      ? ('primary' as const)
      : ('outline' as const),
});

/**
 * Pilulky karty v mriežke. Zostávajúci čas aj počet hlasov nesie sama - na
 * rozdiel od hlavnej karty, kde tie dva údaje stoja vpravo ako stat boxy.
 */
const VotingChips: React.FC<{
  voting: Voting;
  state: CitizenState;
  now: Date;
}> = ({ voting, state, now }) => {
  const days = remainingDays(voting.open_to, now);
  const remaining = remainingLabel(days);
  const votes = `${voting.votes_count} ${getVotesDeclension(voting.votes_count)}`;

  return (
    <VotingBadges>
      {state === 'open' &&
        // Odovzdaný hlas je prvá vec, ktorú má občan na karte prečítať -
        // preto stojí na mieste zostávajúceho času, nie za ním.
        (voting.already_voted ? (
          <VotingBadge tone='success'>
            <CheckIcon sx={{ fontSize: 14 }} />
            {__('Hlasovali ste')}
          </VotingBadge>
        ) : (
          <VotingBadge dot tone={remainingTone(days)}>
            {remaining}
          </VotingBadge>
        ))}

      {state === 'draft' && (
        <VotingBadge tone='warning'>
          {voting.open_from
            ? `${__('Otvára sa')} ${formatDate(new Date(voting.open_from))}`
            : __('Otvorí sa neskôr')}
        </VotingBadge>
      )}

      {state === 'closed' && voting.open_to && (
        <VotingBadge>{`${__('Uzavreté')} ${formatDate(new Date(voting.open_to))}`}</VotingBadge>
      )}

      {/* Pripravované hlasovanie má nula hlasov vždy - „0 hlasov" na karte je
          údaj, ktorý nič nehovorí. */}
      {state !== 'draft' && (
        <VotingBadge>
          {state === 'open' && voting.already_voted
            ? `${remaining} · ${votes}`
            : votes}
        </VotingBadge>
      )}

      {voting.verified_accounts_only && (
        <VotingBadge tone='warning'>{__('Iba overené účty')}</VotingBadge>
      )}
    </VotingBadges>
  );
};

/**
 * Údaj v ráme na hlavnej karte - počet hlasov a dokedy hlasovanie beží.
 *
 * Inšpirované titulnou kartou plánovača: dva tiché rámy vpravo od textu a pod
 * nimi tmavá akcia. `value` je krátke a tučné, `sub` je voliteľná druhá linka
 * (napr. „Zostáva 26 dní" pod dátumom konca).
 */
type HeroStat = { label: string; value: string; sub?: string };

const heroStats = (
  voting: Voting,
  state: CitizenState,
  now: Date,
): HeroStat[] => {
  const votes: HeroStat = {
    label: __('Počet hlasov'),
    value: String(voting.votes_count),
  };

  // Pripravované hlasovanie má nula hlasov vždy, takže „Počet hlasov" tu
  // nehovorí nič - stat box je jediný a nesie dátum otvorenia.
  if (state === 'draft') {
    return [
      {
        label: __('Otvára sa'),
        value: voting.open_from
          ? formatDate(new Date(voting.open_from))
          : __('Neskôr'),
      },
    ];
  }

  if (state === 'closed') {
    return [
      votes,
      {
        label: __('Uzavreté'),
        value: voting.open_to
          ? formatDate(new Date(voting.open_to))
          : __('Bez dátumu'),
      },
    ];
  }

  const days = remainingDays(voting.open_to, now);
  return [
    votes,
    {
      label: __('Prebieha do'),
      value: voting.open_to
        ? formatDate(new Date(voting.open_to))
        : __('Neobmedzene'),
      // Konkrétny odpočet pod dátumom; bez termínu niet čo odpočítavať.
      sub: days === null ? undefined : remainingLabel(days),
    },
  ];
};

const StatBox: React.FC<HeroStat> = ({ label, value, sub }) => (
  <Box
    sx={({ palette, shape }) => ({
      display: 'flex',
      flexDirection: 'column',
      gap: 0.25,
      minWidth: 0,
      px: 1.5,
      py: 1.25,
      border: `1px solid ${palette.divider}`,
      borderRadius: `${shape.borderRadius}px`,
      backgroundColor: palette.background.paper,
    })}
  >
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: 'text.secondary',
      }}
    >
      {label}
    </Typography>
    <Typography
      component='b'
      sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}
    >
      {value}
    </Typography>
    {sub && (
      <Typography variant='caption' color='text.secondary'>
        {sub}
      </Typography>
    )}
  </Box>
);

/** Pilulky hlavnej karty: stav (nemá ho odkiaľ zobrať skupina), účasť, overenie. */
const HeroChips: React.FC<{
  voting: Voting;
  state: CitizenState;
  now: Date;
}> = ({ voting, state, now }) => {
  const status = citizenVotingStatus(voting, now);
  return (
    <VotingBadges>
      <VotingBadge dot tone={status.tone}>
        {status.label}
      </VotingBadge>
      {state === 'open' && voting.already_voted && (
        <VotingBadge tone='success'>
          <CheckIcon sx={{ fontSize: 14 }} />
          {__('Hlasovali ste')}
        </VotingBadge>
      )}
      {voting.verified_accounts_only && (
        <VotingBadge tone='warning'>{__('Iba overené účty')}</VotingBadge>
      )}
    </VotingBadges>
  );
};

/**
 * Hlavná karta - najnovšie hlasovanie, ktoré má zmysel otvoriť.
 *
 * Rozloženie je z titulnej karty plánovača: vľavo text, vpravo dva tiché rámy
 * s číslami (počet hlasov, dokedy beží) a pod nimi tmavá akcia. Od kariet
 * v mriežke sa líši dvoma vecami:
 *
 * - **nekráti text do šírky.** Názov ani popis sa neobmedzujú na počet
 *   znakov, takže na širokej obrazovke bežia cez celú voľnú šírku a
 *   nezalamujú sa skôr, než musia. Šírku im dáva pravý stĺpec (stat boxy majú
 *   pevných ~300 px), nie `maxWidth`. Do výšky strop má - viď
 *   [[HeroDescription]].
 * - **údaje sú v boxoch, nie v pilulkách.** Počet hlasov a zostávajúci čas
 *   nesú stat boxy vpravo, takže v pilulkách vľavo zostáva len stav, účasť
 *   a overenie.
 */
/** Koľko z popisu je na hlavnej karte vidno, kým sa zvyšok schová za prechod. */
const HERO_DESCRIPTION_MAX_HEIGHT = 260;

/** Výška prechodu do bielej na spodku odrezaného popisu. */
const HERO_DESCRIPTION_FADE = 72;

/**
 * Popis na hlavnej karte - **markdown so stropom výšky**.
 *
 * Zadanie hlasovania má aj dve obrazovky textu (kto smie hlasovať, v akých
 * oblastiach, ako sa hlasuje) a karta ho celý vykreslila, takže odtlačila
 * zvyšok zoznamu pod okraj okna. Text sa preto odreže a spodok prejde do
 * farby karty; zvyšok je za tlačidlom, ktoré vedie na detail.
 *
 * **Prechod sa kreslí, len keď je text naozaj odrezaný.** CSS sa na to spýtať
 * nedá, takže sa to meria (`scrollHeight` proti `clientHeight`) - natvrdo
 * kreslený gradient by vybledol aj spodný riadok popisu, ktorý sa celý zmestil.
 * `ResizeObserver` to premeria pri zmene šírky, lebo iná šírka znamená iné
 * zalomenie a s ním inú výšku textu.
 */
const HeroDescription: React.FC<{ value: string }> = ({ value }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [isClipped, setIsClipped] = React.useState(false);

  React.useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const measure = () =>
      setIsClipped(element.scrollHeight > element.clientHeight + 1);

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [value]);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        ref={ref}
        sx={{
          maxHeight: HERO_DESCRIPTION_MAX_HEIGHT,
          overflow: 'hidden',
          '& .wmde-markdown': {
            background: 'transparent',
            color: 'text.secondary',
          },
          // Odstupy v karte drží `gap` stĺpca, markdown by k nim pridal ešte
          // margin prvého a posledného bloku.
          '& .wmde-markdown > :first-of-type': { marginTop: 0 },
          '& .wmde-markdown > :last-child': { marginBottom: 0 },
        }}
      >
        <MarkdownPreview value={value} />
      </Box>

      {isClipped && (
        <Box
          aria-hidden
          sx={(theme) => ({
            position: 'absolute',
            insetInline: 0,
            bottom: 0,
            height: HERO_DESCRIPTION_FADE,
            pointerEvents: 'none',
            // Priehľadná biela, nie `transparent`: to je priehľadná **čierna**
            // a gradient by cez ňu prešiel sivým závojom.
            background: `linear-gradient(to bottom, ${alpha(
              theme.palette.background.paper,
              0,
            )}, ${theme.palette.background.paper})`,
          })}
        />
      )}
    </Box>
  );
};

const HeroVotingCard: React.FC<{
  voting: Voting;
  state: CitizenState;
  now: Date;
}> = ({ voting, state, now }) => {
  const action = cardAction(voting, state);
  const stats = heroStats(voting, state, now);

  return (
    <Card
      sx={{
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'flex-start' },
        gap: { xs: 2, md: 4 },
        p: { xs: 2, sm: 3 },
      }}
    >
      <Stack gap={1.25} sx={{ flex: 1, minWidth: 0 }}>
        <HeroChips voting={voting} state={state} now={now} />

        <Typography
          component='h2'
          sx={{
            fontSize: { xs: 22, sm: 28 },
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          {voting.title}
        </Typography>

        {/* **Hero karta popis kreslí ako markdown**, rovnako ako detail -
            zmestí sa doň odstavec, zoznam aj odkaz ([[HeroDescription]]).
            Malé karty v mriežke to mať nemôžu: sú skrátené na dva riadky
            a `-webkit-line-clamp` reže riadkový obsah, nie bloky, ktoré by
            z markdownu vznikli - tam ide výňatok ako text ([[votings]]). */}
        {voting.description && <HeroDescription value={voting.description} />}
      </Stack>

      {/* Pravý stĺpec: stat boxy a pod nimi tmavá akcia. Pevná šírka drží boxy
          čitateľné a zároveň je to tá šírka, o ktorú sa vľavo zúži text. */}
      <Stack gap={1.25} sx={{ flex: 'none', width: { xs: '100%', md: 300 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns:
              stats.length > 1 ? 'repeat(2, minmax(0, 1fr))' : '1fr',
            gap: 1,
          }}
        >
          {stats.map((stat) => (
            <StatBox key={stat.label} {...stat} />
          ))}
        </Box>

        <LinkButton
          to={action.to}
          variant='dark'
          size='large'
          fullWidth
          endIcon={<ArrowForwardIcon fontSize='small' />}
        >
          {action.label}
        </LinkButton>
      </Stack>
    </Card>
  );
};

/**
 * Karta v mriežke. Akcia je **pod** obsahom a `mt: 'auto'` ju drží na dne
 * karty, takže tlačidlá kariet v jednom riadku sedia na jednej linke aj vtedy,
 * keď má jedna z nich popis a druhá nie.
 */
const VotingCard: React.FC<{
  voting: Voting;
  state: CitizenState;
  now: Date;
}> = ({ voting, state, now }) => {
  const action = cardAction(voting, state);
  const description = plainDescription(voting.description);

  return (
    <Card
      sx={{
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 1,
        p: { xs: 2, sm: 2.5 },
        height: '100%',
      }}
    >
      <VotingChips voting={voting} state={state} now={now} />

      <Typography
        component='b'
        sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.3 }}
      >
        {voting.title}
      </Typography>

      {description && (
        <Typography
          variant='body2'
          color='text.secondary'
          title={description}
          sx={twoLines}
        >
          {description}
        </Typography>
      )}

      {/* Šípka je tá istá ako na hlavnej karte - tlačidlo vedie preč z karty
          na detail, nie je to prepínač na nej. */}
      <LinkButton
        to={action.to}
        variant={action.variant}
        endIcon={<ArrowForwardIcon fontSize='small' />}
        sx={{ mt: 'auto', alignSelf: 'flex-start' }}
      >
        {action.label}
      </LinkButton>
    </Card>
  );
};

const ClosedVotingRow: React.FC<{ voting: Voting; last: boolean }> = ({
  voting,
  last,
}) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    alignItems={{ xs: 'flex-start', sm: 'center' }}
    gap={{ xs: 1, sm: 2 }}
    sx={({ palette }) => ({
      px: { xs: 2, sm: 3 },
      py: 2,
      borderBottom: last ? 'none' : `1px solid ${palette.dividerLight}`,
    })}
  >
    <Stack gap={0.375} sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        component='b'
        sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.35 }}
      >
        {voting.title}
      </Typography>
      <Typography variant='body2' color='text.secondary'>
        {`${voting.open_to ? `${__('Uzavreté')} ${formatDate(new Date(voting.open_to))} · ` : ''}${voting.votes_count} ${getVotesDeclension(voting.votes_count)}`}
      </Typography>
    </Stack>

    {/* „Otvoriť" a detail hlasovania, ako na kartách ([[cardAction]]) -
        v zozname nemá stáť popis, ktorý sľubuje výsledky. Hlasovanie ich
        nemusí zverejňovať vôbec a odkaz by končil na stránke s vysvetlením,
        prečo tam nič nie je; z detailu vedie na výsledky vlastné tlačidlo,
        keď je čo ukázať. */}
    <Typography
      component={Link}
      to={votingDetailPath(voting.voting_id)}
      variant='body2'
      sx={{
        flex: 'none',
        fontWeight: 700,
        color: 'blue.dark',
        textDecoration: 'none',
      }}
    >
      {__('Otvoriť')}
    </Typography>
  </Stack>
);

/**
 * Verejný zoznam hlasovaní - hlavná karta, mriežka kariet a riadky, nie
 * tabuľka.
 *
 * Tabuľka tu bola predloha zo správy hlasovaní a občanovi nesedela: začínala
 * identifikátorom, ktorý mu nič nehovorí, a prebiehajúce hlasovanie v nej
 * vyzeralo rovnako ako to spred roka. Poradie na stránke je poradie
 * naliehavosti: **jedno hlavné hlasovanie s celým textom**, pod ním ostatné
 * prebiehajúce a pripravované ako karty (1 až 4 stĺpce podľa šírky) a
 * ukončené už len ako riadky s odkazom na výsledky.
 *
 * **Stránka nemá strop šírky** a mriežka strop na štyri stĺpce; hlavná karta
 * text nekráti a šírku jej dáva pravý stĺpec so stat boxmi.
 *
 * **Hľadanie ani filter tu zatiaľ nie sú** - hlasovaní je málo a stránka ich
 * ukazuje všetky (viď `LIMIT`).
 *
 * **„Hlasovali ste" je bez dátumu.** `already_voted` je boolean; kedy hlas
 * prišiel, vie až detail, a kvôli jednému riadku v zozname sa naň nepýtame.
 */
const VotingsPage: React.FC = () => {
  const now = React.useMemo(() => new Date(), []);
  const { authState } = useAuth();
  const { setPageMeta } = useLayout();
  const [showAllClosed, setShowAllClosed] = React.useState(false);

  /**
   * **Kým sa neuzavrie prihlásenie, zoznam sa nepýta.**
   *
   * `AuthProvider` je `optional`, takže appka sa vykreslí skôr, než je token
   * na klientovi (`DigiobcanApi.setToken` beží až v `onAuthorize`). Dotaz
   * vypustený v tom okne odíde bez hlavičky a server ho obslúži ako
   * neprihláseného - `getVotings` vtedy pridáva `where public = true`, takže
   * **neverejné hlasovanie zo zoznamu vypadne** a react-query ten výsledok
   * ešte minútu drží. Navonok to vyzerá, že hlasovanie po obnovení stránky
   * zmizlo.
   */
  const isAuthorizing = authState === 'authorizing';

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQ(
    getVotings,
    { limit: LIMIT },
    {
      enabled: !isAuthorizing,
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        if (lastPage.length < LIMIT) {
          return undefined;
        }
        const lastItem = lastPage[lastPage.length - 1];
        return [lastItem.open_from, lastItem.voting_id];
      },
    },
  );

  React.useEffect(() => {
    if (isError) {
      toastError(__('Hlasovania sa nepodarilo načítať.'));
    }
  }, [isError]);

  const all = React.useMemo(
    () => ((data?.pages ?? []).flat() ?? []) as Voting[],
    [data],
  );

  // ── zoskupenie ───────────────────────────────────────────────────────────

  const groups = React.useMemo(() => {
    const byState: Record<VotingState, Voting[]> = {
      open: [],
      draft: [],
      closed: [],
      archived: [],
    };
    all.forEach((voting) => byState[votingState(voting, now)].push(voting));
    return byState;
  }, [all, now]);

  /**
   * Hlavná karta je **najnovšie hlasovanie, ktoré má zmysel otvoriť**: API
   * radí podľa `open_from` zostupne, takže prvé prebiehajúce je zároveň
   * najnovšie. Keď nič neprebieha, vezme sa najbližšie pripravované (v tom
   * poradí je posledné - má najbližší dátum otvorenia) a až nakoniec posledné
   * ukončené; stránka tak nikdy nezačína polovičnou mriežkou bez úvodu.
   */
  const hero = React.useMemo<{
    voting: Voting;
    state: CitizenState;
  } | null>(() => {
    if (groups.open.length) {
      return { voting: groups.open[0], state: 'open' };
    }
    if (groups.draft.length) {
      return { voting: groups.draft[groups.draft.length - 1], state: 'draft' };
    }
    return groups.closed.length
      ? { voting: groups.closed[0], state: 'closed' }
      : null;
  }, [groups]);

  /** Hlavná karta sa v skupine pod sebou už nezopakuje. */
  const withoutHero = (state: CitizenState) =>
    groups[state].filter(
      (voting) => voting.voting_id !== hero?.voting.voting_id,
    );

  const open = withoutHero('open');
  const draft = withoutHero('draft');
  const closed = withoutHero('closed');

  // Počet vedľa titulu. Poctivý je len nad načítaným celkom, tak sa pri ďalšej
  // strane nekreslí vôbec - „100 hlasovaní" pri stodvadsiatich klame.
  const count = (hero ? 1 : 0) + open.length + draft.length + closed.length;

  React.useEffect(() => {
    if (hasNextPage) {
      setPageMeta(null);
      return;
    }

    setPageMeta(
      `${count} ${declension(count, [
        __('hlasovanie'),
        __('hlasovania'),
        __('hlasovaní'),
      ])}`,
    );

    return () => setPageMeta(null);
  }, [setPageMeta, hasNextPage, count]);

  // Vypnutý dotaz **nie je** `isLoading` (react-query mu dá `fetchStatus: idle`),
  // takže bez `isAuthorizing` by sa počas prihlasovania bliskol prázdny stav.
  const isBusy = isLoading || isAuthorizing;

  // Prázdno je, keď nie je ani hlavná karta - tá sa berie z prvej neprázdnej
  // skupiny, takže jej absencia znamená, že nezostalo nič.
  const isEmpty = !isBusy && !hero;

  const closedShown = showAllClosed ? closed : closed.slice(0, CLOSED_PREVIEW);

  return (
    <Box sx={{ backgroundColor: 'background.canvas', minHeight: '100%' }}>
      <Stack gap={2.5} sx={{ px: { xs: 1.75, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>
        {isBusy && (
          <>
            <Skeleton variant='rounded' height={196} />
            <CardGrid>
              <Skeleton variant='rounded' height={168} />
              <Skeleton variant='rounded' height={168} />
              <Skeleton variant='rounded' height={168} />
              <Skeleton variant='rounded' height={168} />
            </CardGrid>
          </>
        )}

        {isEmpty && (
          <EmptyState
            icon='list'
            title={__('Žiadne hlasovania')}
            description={__('Momentálne nie je vypísané žiadne hlasovanie.')}
          />
        )}

        {hero && (
          <HeroVotingCard voting={hero.voting} state={hero.state} now={now} />
        )}

        {open.length > 0 && (
          <Stack gap={1.25}>
            <SectionTitle>
              {hero?.state === 'open'
                ? __('Ďalšie prebiehajúce')
                : __('Prebiehajúce')}
            </SectionTitle>
            <CardGrid>
              {open.map((voting) => (
                <VotingCard
                  key={voting.voting_id}
                  voting={voting}
                  state='open'
                  now={now}
                />
              ))}
            </CardGrid>
          </Stack>
        )}

        {draft.length > 0 && (
          <Stack gap={1.25}>
            <SectionTitle>{__('Pripravované')}</SectionTitle>
            <CardGrid>
              {draft.map((voting) => (
                <VotingCard
                  key={voting.voting_id}
                  voting={voting}
                  state='draft'
                  now={now}
                />
              ))}
            </CardGrid>
          </Stack>
        )}

        {closed.length > 0 && (
          <Stack gap={1.25}>
            <SectionTitle>{__('Ukončené')}</SectionTitle>
            {/* `gap: 0` je zámer: `Card` má z témy 16 px medzeru, a tá by
                riadky odtrhla od deliacich liniek. */}
            <Card sx={{ p: 0, gap: 0, overflow: 'hidden' }}>
              {closedShown.map((voting, index) => (
                <ClosedVotingRow
                  key={voting.voting_id}
                  voting={voting}
                  last={
                    index === closedShown.length - 1 &&
                    closed.length <= CLOSED_PREVIEW
                  }
                />
              ))}

              {/* Ukončených býva desiatky a sú to tie najmenej naliehavé
                  položky stránky - preto sa vypíše len začiatok. Počet v texte
                  je počet načítaných, tak sa pri ďalšej strane netvrdí
                  „všetkých". */}
              {closed.length > CLOSED_PREVIEW && !showAllClosed && (
                <Button
                  variant='text'
                  onClick={() => setShowAllClosed(true)}
                  sx={{ alignSelf: 'flex-start', mx: { xs: 1, sm: 2 }, my: 1 }}
                >
                  {hasNextPage
                    ? __('Zobraziť ďalšie ukončené')
                    : `${__('Zobraziť všetkých')} ${closed.length} ${__('ukončených')}`}
                </Button>
              )}
            </Card>
          </Stack>
        )}

        {hasNextPage && (
          <Button
            variant='outline'
            loading={isFetchingNextPage}
            onClick={() => fetchNextPage()}
            sx={{ alignSelf: 'center' }}
          >
            {__('Zobraziť ďalšie hlasovania')}
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default VotingsPage;
